export type PrometheusBeaconEventsDTO = Record<string, number>;

/*
 * ClientEventManager is a basic collector for client-side events.
 * It is designed to store RUM events as eventual prometheus metrics and send
 * them to the server on page unload. May not work 100% of the time and that's okay.
 * Assumed to run in browser but browser APIs are not assumed to exist at runtime.
 * Should run harmlessly in a server-side setting.
 * Activated in clientHooks.ts and sends events to /api/event endpoint as {"type:name": count...}
 */
export class ClientEventManager {
  private beaconEnabled = false;
  private logEnabled = false;
  private events: PrometheusBeaconEventsDTO;
  private readonly listenerType: string = 'visibilitychange';
  private readonly listenerEvent: EventListenerOrEventListenerObject;

  constructor(
    private readonly window?: Window,
    private readonly navigator?: Navigator,
    private readonly document?: Document,
    private readonly console?: Console
  ) {
    this.events = {};
    this.listenerEvent = (_event: Event) => {
      if (this.document?.visibilityState === 'hidden') {
        this.sendEvents();
      }
    };
  }

  isBeaconEnabled(): boolean {
    return this.beaconEnabled;
  }

  isLogEnabled(): boolean {
    return this.logEnabled;
  }

  enableBeacon(isEnabled: boolean): boolean {
    const originalBeaconEnabled = this.beaconEnabled;
    this.beaconEnabled = isEnabled && this.window !== undefined && !!this.navigator?.sendBeacon;
    if (originalBeaconEnabled !== this.beaconEnabled && this.window !== undefined) {
      if (this.beaconEnabled) {
        this.window.addEventListener(this.listenerType, this.listenerEvent);
      } else {
        this.window.removeEventListener(this.listenerType, this.listenerEvent);
      }
    }
    return this.beaconEnabled;
  }

  enableLog(isEnabled: boolean): boolean {
    this.logEnabled = isEnabled && this.console !== undefined;
    return this.logEnabled;
  }

  queueEvent(key: string): void {
    const eventCount = this.events[key] ?? 0;
    this.events[key] = eventCount + 1;
    if (this.logEnabled) {
      this.console.log('Client Event Queued', key);
    }
  }

  sendEvents(): void {
    if (this.beaconEnabled && Object.keys(this.events).length > 0) {
      this.navigator.sendBeacon('/api/event', JSON.stringify(this.events));
    }
    if (this.logEnabled) {
      this.console.log('Client Events Sent', this.events);
    }
    this.events = {};
  }
}

// Use module scope to allow singleton instance fetching
// getClientEventManager() used to lazy load and not export instance directly.
let instanceClientEventManager: ClientEventManager;
export const getClientEventManager = () => {
  if (instanceClientEventManager === undefined) {
    // Attempt to inject browser APIs from global scope
    const _window = window ?? undefined;
    const _navigator = navigator ?? undefined;
    const _document = document ?? undefined;
    const _console = console ?? undefined;
    instanceClientEventManager = new ClientEventManager(_window, _navigator, _document, _console);
    instanceClientEventManager.enableBeacon(true);
    instanceClientEventManager.enableLog(false);
    if (_window !== undefined && _window['ClientEventManager'] === undefined) {
      _window['ClientEventManager'] = instanceClientEventManager;
    }
  }
  return instanceClientEventManager;
};
